import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { getUserProfile, updateUserProfile } from '../../api';
import { UserProfile } from '../../types';
import LoadingButton, { keyMap } from '../widgets/components/ButtonWithLoader';

interface ProfileFormProps {
  onSave?: () => void;
}

export const ProfileForm: React.FC<ProfileFormProps> = ({ onSave }) => {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [initialLoading, setInitialLoading] = useState<boolean>(true);
  const [errors, setErrors] = useState<{ userName?: string; phone?: string }>({});

  // Load profile on mount
  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const data = await getUserProfile();
      setUser(data);
    } catch (error: any) {
      toast.error(error?.message || 'Failed to load profile'.toTranslatedDisplay());
    } finally {
      setInitialLoading(false);
    }
  };

  const validate = () => {
    const newErrors: typeof errors = {};

    if (!user?.userName?.trim()) {
      newErrors.userName = 'User name is required'.toTranslatedDisplay();
    }

    if (user?.phone && user.phone.length < 6) {
      newErrors.phone = 'Phone number is too short'.toTranslatedDisplay();
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setUser(prev => prev ? { ...prev, [name]: value } : prev);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!user) return;
    if (!validate()) return;

    setLoading(true);

    try {
      await updateUserProfile({
        userName: user.userName,
        phone: user.phone,
      });

      toast.success('Profile updated successfully!'.toTranslatedDisplay());

      if (onSave) {
        onSave();
      }

    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to update profile'.toTranslatedDisplay());
      console.error('Error updating profile:', error);
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="text-center py-5">
        <span className="spinner-border text-primary"></span>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div>
      <form onSubmit={handleSubmit}>

        {/* User Name */}
        <div className="mb-3">
          <label className="form-label">{'User Name'.toTranslatedDisplay()}</label>
          <input
            type="text"
            className={`form-control ${errors.userName ? 'is-invalid' : ''}`}
            placeholder={'User Name'.toTranslatedDisplay()}
            name="userName"
            value={user.userName || ''}
            onChange={handleInputChange}
            required
          />
          {errors.userName && (
            <div className="invalid-feedback">{errors.userName}</div>
          )}
        </div>

        {/* Email (Disabled) */}
        <div className="mb-3">
          <label className="form-label">{'Email'.toTranslatedDisplay()}</label>
          <input
            type="email"
            className="form-control"
            value={user.email}
            disabled
          />
        </div>

        {/* Phone */}
        <div className="mb-3">
          <label className="form-label">{'Phone'.toTranslatedDisplay()}</label>
          <input
            type="text"
            className={`form-control ${errors.phone ? 'is-invalid' : ''}`}
            placeholder={'Phone'.toTranslatedDisplay()}
            name="phone"
            value={user.phone || ''}
            onChange={handleInputChange}
          />
          {errors.phone && (
            <div className="invalid-feedback">{errors.phone}</div>
          )}
        </div>

        {/* Save Button */}
        <LoadingButton
          className="mt-3"
          isLoading={loading}
          onClick={handleSubmit}
          text={'Save Changes'.toTranslatedDisplay()}
          type="submit"
          shortcutTip="Ctrl + S"
          shortcutKeys={[keyMap.SAVE]}
        />

      </form>
    </div>
  );
};
